import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, authAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import { Drawer } from '../shared/DrawerManager';
import './UserManagement.css';

const INTERNAL_ROLES = ['admin', 'recruiter', 'interviewer', 'trainer', 'stakeholder'];

const UserManagement = ({ onNavigate, currentView, embedded = false }) => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'pending'
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Approval drawer state
  const [showApproveDrawer, setShowApproveDrawer] = useState(false);
  const [userToApprove, setUserToApprove] = useState(null);
  const [approveRole, setApproveRole] = useState('recruiter');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'candidate',
    company: '',
  });

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    loadAll();
  }, []);

  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, roleFilter, statusFilter]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, pendingRes] = await Promise.all([
        usersAPI.getAll(),
        authAPI.getPendingUsers()
      ]);
      setUsers(usersRes.data.users || []);
      setPendingUsers(pendingRes.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setCurrentUser(null);
    setFormData({ email: '', name: '', role: 'candidate', company: '' });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setCurrentUser(user);
    setFormData({
      email: user.email,
      name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      role: user.role,
      company: user.company || '',
    });
    setShowModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError(null);
      await usersAPI.delete(userToDelete._id);
      setSuccess(`User ${userToDelete.email} archived successfully`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      loadAll();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (modalMode === 'create') {
        await usersAPI.create(formData);
        setSuccess('User created successfully');
      } else {
        await usersAPI.update(currentUser._id, formData);
        setSuccess('User updated successfully');
      }
      setShowModal(false);
      loadAll();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save user:', err);
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openApproveDrawer = (user) => {
    setUserToApprove(user);
    setApproveRole('recruiter');
    setShowApproveDrawer(true);
  };

  const handleApprove = async () => {
    try {
      setApprovalLoading(true);
      await authAPI.approveUser(userToApprove._id, approveRole);
      setSuccess(`${userToApprove.email} approved as ${approveRole}`);
      setShowApproveDrawer(false);
      setUserToApprove(null);
      loadAll();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleReject = async (user) => {
    if (!window.confirm(`Reject and archive ${user.email}?`)) return;
    try {
      await authAPI.rejectUser(user._id);
      setSuccess(`${user.email} rejected`);
      loadAll();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject user');
    }
  };

  const handleReactivate = async (user) => {
    if (!window.confirm(`Reactivate ${user.email}?`)) return;
    try {
      await authAPI.reactivateUser(user._id);
      setSuccess(`${user.email} reactivated`);
      loadAll();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reactivate user');
    }
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  if (!isAdmin()) {
    return (
      <div className="user-management">
        <div className="error-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {!embedded && <AdminHeader currentView={currentView} onNavigate={onNavigate} />}
      <div className="user-management-content">
        {!embedded && (
          <div className="management-header">
            <h2>User Management</h2>
            <button className="cyber-btn primary" onClick={handleCreateUser}>
              + Create User
            </button>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="success-banner">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="user-tabs" style={{ display: 'flex', gap: '0', marginBottom: '1rem', borderBottom: '2px solid #e5e7eb' }}>
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            style={{
              padding: '0.6rem 1.25rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'all' ? '600' : '400',
              borderBottom: activeTab === 'all' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'all' ? '#2563eb' : '#6b7280',
              marginBottom: '-2px'
            }}
          >
            All Users ({users.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            style={{
              padding: '0.6rem 1.25rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'pending' ? '600' : '400',
              borderBottom: activeTab === 'pending' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'pending' ? '#2563eb' : '#6b7280',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            Pending Approval
            {pendingUsers.length > 0 && (
              <span style={{
                background: '#ef4444',
                color: '#fff',
                borderRadius: '9999px',
                padding: '0 6px',
                fontSize: '0.7rem',
                fontWeight: '700'
              }}>
                {pendingUsers.length}
              </span>
            )}
          </button>
        </div>

        {/* PENDING APPROVALS TAB */}
        {activeTab === 'pending' && (
          <div className="users-table-section terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
              </div>
              <span className="terminal-title">pending.approvals</span>
            </div>
            <div className="table-wrapper">
              {pendingUsers.length === 0 ? (
                <div className="empty-state">
                  <p>No users pending approval</p>
                </div>
              ) : (
                <table className="cyber-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Job Title</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((user) => (
                      <tr key={user._id}>
                        <td className="text-mono">{user.email}</td>
                        <td>{user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '-'}</td>
                        <td>{user.metadata?.jobTitle || '-'}</td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="cyber-btn-sm"
                              style={{ background: '#16a34a', color: '#fff' }}
                              onClick={() => openApproveDrawer(user)}
                            >
                              Approve
                            </button>
                            <button
                              className="cyber-btn-sm danger"
                              onClick={() => handleReject(user)}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ALL USERS TAB */}
        {activeTab === 'all' && (
          <>
            {/* Filters */}
            <div className="filters-section terminal-card">
              <div className="terminal-header">
                <div className="terminal-dots">
                  <span className="terminal-dot red"></span>
                  <span className="terminal-dot yellow"></span>
                  <span className="terminal-dot green"></span>
                </div>
                <span className="terminal-title">filters</span>
              </div>
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Search</label>
                  <input
                    type="text"
                    className="cyber-input"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Role</label>
                  <select
                    className="cyber-input"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="all">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="recruiter">Recruiter</option>
                    <option value="interviewer">Interviewer</option>
                    <option value="trainer">Trainer</option>
                    <option value="stakeholder">Stakeholder</option>
                    <option value="candidate">Candidate</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    className="cyber-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="invited">Invited</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="users-table-section terminal-card">
              <div className="terminal-header">
                <div className="terminal-dots">
                  <span className="terminal-dot red"></span>
                  <span className="terminal-dot yellow"></span>
                  <span className="terminal-dot green"></span>
                </div>
                <span className="terminal-title">users.list</span>
              </div>
              <div className="table-wrapper">
                {currentUsers.length === 0 ? (
                  <div className="empty-state">
                    <p>No users found</p>
                  </div>
                ) : (
                  <table className="cyber-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.map((user) => (
                        <tr key={user._id}>
                          <td className="text-mono">{user.email}</td>
                          <td>
                            {user.firstName
                              ? `${user.firstName} ${user.lastName || ''}`.trim()
                              : user.name || '-'}
                          </td>
                          <td>
                            <span className={`cyber-badge ${getRoleBadgeClass(user.role)}`}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            <span className={`cyber-badge ${getStatusBadgeClass(user.status)}`}>
                              {(user.status || 'invited').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleDateString()
                              : 'Never'}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="cyber-btn-sm"
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </button>
                              {user.status === 'pending' && (
                                <button
                                  className="cyber-btn-sm"
                                  style={{ background: '#16a34a', color: '#fff' }}
                                  onClick={() => openApproveDrawer(user)}
                                >
                                  Approve
                                </button>
                              )}
                              {user.status === 'suspended' && (
                                <button
                                  className="cyber-btn-sm"
                                  style={{ background: '#16a34a', color: '#fff' }}
                                  onClick={() => handleReactivate(user)}
                                >
                                  Reactivate
                                </button>
                              )}
                              <button
                                className="cyber-btn-sm danger"
                                onClick={() => handleDeleteClick(user)}
                              >
                                Archive
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="cyber-btn-sm"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`page-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    className="cyber-btn-sm"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Approve User Drawer */}
        <Drawer
          isOpen={showApproveDrawer}
          onClose={() => setShowApproveDrawer(false)}
          title="Approve User Access"
          width="sm"
          footer={
            <>
              <button
                type="button"
                className="drawer-btn drawer-btn-secondary"
                onClick={() => setShowApproveDrawer(false)}
                disabled={approvalLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="drawer-btn drawer-btn-primary"
                onClick={handleApprove}
                disabled={approvalLoading}
              >
                {approvalLoading ? 'Approving...' : 'Approve & Notify'}
              </button>
            </>
          }
        >
          {userToApprove && (
            <div style={{ padding: '0.5rem 0' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{userToApprove.email}</p>
                {userToApprove.firstName && (
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    {userToApprove.firstName} {userToApprove.lastName || ''}
                  </p>
                )}
                {userToApprove.metadata?.jobTitle && (
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    {userToApprove.metadata.jobTitle}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label style={{ fontWeight: '500', display: 'block', marginBottom: '0.4rem' }}>
                  Assign Role
                </label>
                <select
                  className="cyber-input"
                  value={approveRole}
                  onChange={(e) => setApproveRole(e.target.value)}
                >
                  {INTERNAL_ROLES.map(r => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '1rem' }}>
                The user will receive an email notification and can log in immediately.
              </p>
            </div>
          )}
        </Drawer>

        {/* Create/Edit Drawer */}
        <Drawer
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalMode === 'create' ? 'Create User' : 'Edit User'}
          width="md"
          footer={
            <>
              <button type="button" className="drawer-btn drawer-btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" form="user-form" className="drawer-btn drawer-btn-primary">
                {modalMode === 'create' ? 'Create' : 'Update'}
              </button>
            </>
          }
        >
          <form id="user-form" onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                className="cyber-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={modalMode === 'edit'}
              />
            </div>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                className="cyber-input"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Role *</label>
              <select
                name="role"
                className="cyber-input"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="candidate">Candidate</option>
                <option value="recruiter">Recruiter</option>
                <option value="interviewer">Interviewer</option>
                <option value="trainer">Trainer</option>
                <option value="stakeholder">Stakeholder</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                name="company"
                className="cyber-input"
                value={formData.company}
                onChange={handleInputChange}
              />
            </div>
          </form>
        </Drawer>

        {/* Delete Confirmation Drawer */}
        <Drawer
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Archive User"
          width="sm"
          footer={
            <>
              <button className="drawer-btn drawer-btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="drawer-btn drawer-btn-danger" onClick={handleDeleteConfirm}>
                Archive User
              </button>
            </>
          }
        >
          <div className="confirm-content">
            <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#374151' }}>
              Are you sure you want to archive <strong>{userToDelete?.email}</strong>?
            </p>
            <p style={{
              padding: '0.75rem',
              background: '#FFF7ED',
              border: '1px solid #FED7AA',
              borderRadius: '6px',
              color: '#9A3412',
              fontSize: '0.875rem',
              margin: 0
            }}>
              This user will be deactivated and unable to log in. Their data (jobs, applications, interviews) will be preserved. An admin can reactivate them later.
            </p>
          </div>
        </Drawer>
      </div>
    </div>
  );
};

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'admin': return 'danger';
    case 'recruiter': return 'info';
    case 'interviewer': return 'info';
    case 'trainer': return 'info';
    case 'candidate': return 'success';
    default: return '';
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'suspended': return 'danger';
    case 'pending': return 'warning';
    case 'archived': return 'danger';
    default: return 'warning';
  }
};

export default UserManagement;
